import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    Avatar,
    Autocomplete,
} from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import DeleteIcon from '@mui/icons-material/Delete';
import BusinessIcon from '@mui/icons-material/Business';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import israelCities from '../../data/israelCities';

const fieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: '10px',
        fontFamily: "'Heebo', sans-serif",
        fontSize: '0.9rem',
        '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(30,58,95,0.35)',
        },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1e3a5f',
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': { fontFamily: "'Heebo', sans-serif", fontSize: '0.88rem' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#1e3a5f' },
};

/**
 * OrganizationSetupForm - Dummy Component (רק UI!)
 */
const OrganizationSetupForm = ({
    name,
    onNameChange,
    country,
    selectedCity,
    onCityChange,
    street,
    onStreetChange,
    houseNumber,
    onHouseNumberChange,
    logoUrl,
    uploading,
    isSubmitting,
    validationError,
    onLogoUpload,
    onLogoDelete,
    headerTitle = 'ברוכים הבאים!',
    headerSubtitle = 'בואו נגדיר יחד את בית הכנסת שלכם',
}) => {
    const handleFileSelect = (e) => {
        const file = e.target.files?.[0];
        if (file) onLogoUpload(file);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>

            {/* ── Welcome header ── */}
            <Box sx={{ mb: 2.5 }}>
                <Typography sx={{
                    fontSize: '1.25rem', fontWeight: 800,
                    color: '#1e3a5f', fontFamily: "'Heebo', sans-serif",
                    lineHeight: 1.3, mb: 0.4,
                }}>
                    {headerTitle}
                </Typography>
                <Typography sx={{
                    fontSize: '0.85rem', color: '#6b7a8d',
                    fontFamily: "'Heebo', sans-serif",
                }}>
                    {headerSubtitle}
                </Typography>
            </Box>

            {/* ── 2-column layout ── */}
            <Box sx={{ display: 'grid', gridTemplateColumns: '152px 1fr', gap: '24px', alignItems: 'start' }}>

                {/* ── Left column: Logo Upload ── */}
                <Box sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.25,
                    pt: 0.5, pb: 1,
                    border: '1.5px dashed rgba(30,58,95,0.18)',
                    borderRadius: '14px',
                    bgcolor: 'rgba(30,58,95,0.02)',
                    px: 1.5, py: 2,
                    transition: 'border-color 0.2s',
                    '&:hover': { borderColor: 'rgba(30,58,95,0.3)' },
                }}>
                    {logoUrl ? (
                        <Avatar
                            src={logoUrl}
                            sx={{
                                width: 68, height: 68,
                                border: '3px solid rgba(184,134,11,0.35)',
                                boxShadow: '0 4px 14px rgba(0,0,0,0.1)',
                            }}
                        />
                    ) : (
                        <Box sx={{
                            width: 68, height: 68, borderRadius: '50%',
                            bgcolor: 'rgba(30,58,95,0.07)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                        }}>
                            <BusinessIcon sx={{ fontSize: 30, color: 'rgba(30,58,95,0.28)' }} />
                        </Box>
                    )}

                    <Typography sx={{
                        fontSize: '0.72rem', color: '#96a3b0',
                        fontFamily: "'Heebo', sans-serif",
                        textAlign: 'center', lineHeight: 1.4,
                    }}>
                        לוגו בית הכנסת
                        <br />
                        <span style={{ color: '#b0b9c4' }}>(אופציונלי)</span>
                    </Typography>

                    <Button
                        component="label"
                        variant="outlined"
                        size="small"
                        startIcon={uploading
                            ? <CircularProgress size={13} />
                            : <CloudUploadIcon sx={{ ml: 0.5, fontSize: 15 }} />}
                        disabled={uploading || isSubmitting}
                        sx={{
                            borderColor: 'rgba(30,58,95,0.25)', color: '#1e3a5f',
                            borderRadius: '8px', fontFamily: "'Heebo', sans-serif",
                            fontSize: '0.75rem', textTransform: 'none', px: 1.5, py: 0.55,
                            width: '100%',
                            '&:hover': {
                                borderColor: '#1e3a5f',
                                bgcolor: 'rgba(30,58,95,0.04)',
                            },
                        }}
                    >
                        {uploading ? 'מעלה...' : 'העלה תמונה'}
                        <input type="file" hidden accept="image/*" onChange={handleFileSelect} />
                    </Button>

                    {logoUrl && !uploading && (
                        <Button
                            variant="text"
                            color="error"
                            size="small"
                            startIcon={<DeleteIcon sx={{ ml: 0.25, fontSize: 14 }} />}
                            onClick={onLogoDelete}
                            disabled={isSubmitting}
                            sx={{
                                fontFamily: "'Heebo', sans-serif",
                                fontSize: '0.73rem', textTransform: 'none',
                                py: 0.3, px: 0.75,
                            }}
                        >
                            הסר
                        </Button>
                    )}
                </Box>

                {/* ── Right column: Name + Address ── */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

                    {/* Organization name */}
                    <Box>
                        <Typography sx={{
                            fontSize: '0.8rem', fontWeight: 700,
                            color: '#1e3a5f', mb: 0.4,
                            fontFamily: "'Heebo', sans-serif",
                        }}>
                            שם בית הכנסת *
                        </Typography>
                        <Typography sx={{
                            fontSize: '0.74rem', color: '#96a3b0', mb: 0.8,
                            fontFamily: "'Heebo', sans-serif",
                        }}>
                            שם זה יופיע על השלטים הדיגיטליים שלכם
                        </Typography>
                        <TextField
                            fullWidth
                            size="small"
                            required
                            autoFocus
                            placeholder='לדוגמה: בית כנסת "שערי ציון"'
                            value={name}
                            onChange={(e) => onNameChange(e.target.value)}
                            sx={fieldSx}
                        />
                    </Box>

                    {/* Address section */}
                    <Box sx={{
                        border: '1px solid rgba(184,134,11,0.25)',
                        borderRadius: '14px',
                        p: '16px 16px 16px 20px',
                        bgcolor: 'rgba(245,208,96,0.04)',
                        position: 'relative',
                        overflow: 'hidden',
                    }}>
                        {/* Gold right accent bar */}
                        <Box sx={{
                            position: 'absolute',
                            right: 0, top: 14, bottom: 14,
                            width: 3,
                            background: 'linear-gradient(180deg, #9a7209, #f5d060, #9a7209)',
                            borderRadius: '0 14px 14px 0',
                        }} />

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.4 }}>
                            <LocationOnIcon sx={{ fontSize: 16, color: '#9a7209' }} />
                            <Typography sx={{
                                fontSize: '0.86rem', fontWeight: 700,
                                color: '#1e3a5f', fontFamily: "'Heebo', sans-serif",
                            }}>
                                כתובת בית הכנסת
                            </Typography>
                        </Box>

                        <Typography sx={{
                            fontSize: '0.74rem', color: '#8a9ab0',
                            fontFamily: "'Heebo', sans-serif",
                            lineHeight: 1.5, mb: 1.75,
                        }}>
                            הכתובת משמשת לחישוב זמני תפילה, שקיעה ונץ מדויקים עבור המיקום שלכם
                        </Typography>

                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                            {/* Country + City */}
                            <Box sx={{ display: 'flex', gap: 1.25 }}>
                                <TextField
                                    size="small"
                                    label="מדינה"
                                    value={country}
                                    disabled
                                    sx={{ ...fieldSx, flex: 1 }}
                                />
                                <Autocomplete
                                    options={israelCities}
                                    getOptionLabel={(option) => option.name}
                                    value={selectedCity}
                                    onChange={(event, newValue) => onCityChange(newValue)}
                                    noOptionsText="לא נמצאה עיר"
                                    sx={{ flex: 2 }}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            required
                                            size="small"
                                            label="עיר"
                                            placeholder="הקלד שם עיר..."
                                            sx={fieldSx}
                                        />
                                    )}
                                />
                            </Box>

                            {/* Street + House number */}
                            <Box sx={{ display: 'flex', gap: 1.25 }}>
                                <TextField
                                    size="small"
                                    required
                                    label="רחוב"
                                    value={street}
                                    onChange={(e) => onStreetChange(e.target.value)}
                                    sx={{ ...fieldSx, flex: 3 }}
                                />
                                <TextField
                                    size="small"
                                    required
                                    label="מס׳ בית"
                                    value={houseNumber}
                                    onChange={(e) => onHouseNumberChange(e.target.value)}
                                    sx={{ ...fieldSx, flex: 1 }}
                                />
                            </Box>
                        </Box>
                    </Box>

                </Box>
                {/* end right column */}

            </Box>
            {/* end 2-column grid */}

            {/* ── Validation Error ── */}
            {validationError && (
                <Alert
                    severity="error"
                    sx={{ mt: 2, borderRadius: '10px', fontFamily: "'Heebo', sans-serif", fontSize: '0.85rem' }}
                >
                    {validationError}
                </Alert>
            )}
        </Box>
    );
};

export default OrganizationSetupForm;
